import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import { alpha } from '@mui/material/styles';
import { usePresenceActions } from '../context/PresenceContext';
import RowPresenceCell from '../components/Presence/RowPresenceCell';

/** Field of the marker column. csvExport's NON_DATA_FIELDS skips it. */
export const PRESENCE_FIELD = '__presence';

/** Class RowPresenceCell puts on its content while someone else is on the row. */
export const PRESENCE_ACTIVE_CLASS = 'presence-row-active';

/**
 * Spread into a grid's `sx`: a very subtle brand-pink (secondary.main) tint on
 * rows someone else is on. Pure CSS via :has(), so the page itself never
 * re-renders for it. A gradient *image* rather than backgroundColor, so it
 * layers over the selected / hover / status-highlight colours instead of
 * replacing them. Light/dark alphas per design.md's row-highlight rule.
 */
export const PRESENCE_ROW_SX = {
  [`& .MuiDataGrid-row:has(.${PRESENCE_ACTIVE_CLASS})`]: {
    backgroundImage: (theme) => {
      const tint = alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.1 : 0.06);
      return `linear-gradient(${tint}, ${tint})`;
    },
  },
};

/**
 * The grid's column list with the marker column FIRST -- before the checkbox.
 * DataGrid always puts its checkbox column first unless the list places it
 * explicitly, so grids with `checkboxSelection` get it right after the marker.
 * Wrap in useMemo at the call site; pass the same flag as the grid's prop.
 */
export const withPresenceColumn = (presenceColumn, columns, { checkboxSelection = false } = {}) => (
  checkboxSelection
    ? [presenceColumn, GRID_CHECKBOX_SELECTION_COL_DEF, ...columns]
    : [presenceColumn, ...columns]
);

/**
 * Live "who is on which row" for one DataGrid.
 *
 *   const rowPresence = useGridPresence('invoices');
 *   columns = useMemo(() => withPresenceColumn(rowPresence.column, columns, { checkboxSelection: true }), ...);
 *   onRowClick={(p, e) => { rowPresence.onRowClick(p); ... }}
 *   onRowSelectionModelChange={(ids) => { rowPresence.onSelectionChange(ids); ... }}
 *   rowPresence.setEditing(id)   // edit dialog opened for row `id`
 *   rowPresence.setEditing(null) // ... and closed
 *
 * This tab's focus on the grid is the union of the clicked row, the checked
 * rows and the row being edited; it is announced over the shared presence
 * socket and cleared when the grid unmounts (tab switch, navigation).
 * `grid` must be unique per page and stable -- it scopes the row ids.
 */
export const useGridPresence = (grid) => {
  const { setFocus, clearFocus } = usePresenceActions();
  const [clickedId, setClickedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const rows = [];
    if (editingId != null) rows.push(editingId);
    if (clickedId != null) rows.push(clickedId);
    rows.push(...selectedIds);
    setFocus(grid, rows, editingId);
  }, [grid, clickedId, selectedIds, editingId, setFocus]);

  useEffect(() => () => clearFocus(grid), [grid, clearFocus]);

  // Clicking the same row again un-focuses it -- on read-only grids (the
  // summaries) a click is the only signal, so it needs a way back out.
  const onRowClick = useCallback((params) => {
    const id = params?.id ?? null;
    setClickedId((prev) => (prev != null && id != null && String(prev) === String(id) ? null : id));
  }, []);
  const onSelectionChange = useCallback((ids) => setSelectedIds(Array.isArray(ids) ? ids : []), []);
  const setEditing = useCallback((id) => setEditingId(id ?? null), []);

  const column = useMemo(() => ({
    field: PRESENCE_FIELD,
    headerName: '',
    width: 40,
    minWidth: 40,
    sortable: false,
    filterable: false,
    hideable: false,
    resizable: false,
    disableColumnMenu: true,
    disableExport: true,
    disableReorder: true,
    align: 'center',
    renderCell: (params) => <RowPresenceCell grid={grid} rowId={params.id} />,
  }), [grid]);

  return useMemo(
    () => ({ column, onRowClick, onSelectionChange, setEditing, editingId }),
    [column, onRowClick, onSelectionChange, setEditing, editingId],
  );
};
